export const ZIP_EXPORT_DB = "dzp-zip-export";
export const FILES_STORE = "files";
export const CHUNKS_STORE = "zipChunks";

/**
 * A Blob where the browser can store one, raw bytes where it cannot. Blobs are the
 * better record: IndexedDB hands them back as a lazy, usually disk-backed handle, so
 * reading one back streams instead of materialising the whole file in the JS heap.
 * See supportsBlobStorage for when the byte fallback is used instead.
 */
export type StoredFileData = Blob | Uint8Array;

export type StoredFile = {
  name: string;
  data: StoredFileData;
};

/** Joins streamed parts into the single typed array that goes into IndexedDB */
export function concatBytes(parts: Uint8Array[], totalBytes: number): Uint8Array {
  const combined = new Uint8Array(totalBytes);
  let offset = 0;
  for (const part of parts) {
    combined.set(part, offset);
    offset += part.byteLength;
  }
  return combined;
}

/**
 * IndexedDB reports failures as a bare DOMException that is null when a transaction
 * aborts without a failing request, so both the exception name and what was being
 * written have to be folded into the message to survive the trip to the console.
 */
function idbFailure(
  operation: string,
  error: DOMException | null,
  context: Record<string, string | number> = {}
): Error {
  const reason = error ? `${error.name}: ${error.message}` : "no DOMException reported";
  const contextParts = Object.entries(context).map(([key, value]) => `${key}=${value}`);
  const suffix = contextParts.length > 0 ? ` [${contextParts.join(", ")}]` : "";
  return new Error(`IndexedDB ${operation} failed (${reason})${suffix}`, {
    cause: error,
  });
}

/** Size is the number that matters when a write is rejected, never the contents */
function describeStoredValue(value: unknown): string {
  if (value instanceof Uint8Array) {
    return `Uint8Array(${value.byteLength} bytes)`;
  }
  if (value instanceof Blob) {
    return `Blob(${value.size} bytes)`;
  }
  if (value && typeof value === "object" && "data" in value) {
    return `StoredFile(${describeStoredValue(value.data)})`;
  }
  return typeof value;
}

function requestToPromise<T>(
  request: IDBRequest<T>,
  operation: string,
  context: Record<string, string | number> = {}
): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(idbFailure(operation, request.error, context));
  });
}

export function openZipExportDb(): Promise<IDBDatabase> {
  const openRequest = indexedDB.open(ZIP_EXPORT_DB, 1);
  openRequest.onupgradeneeded = () => {
    const db = openRequest.result;
    if (!db.objectStoreNames.contains(FILES_STORE)) {
      db.createObjectStore(FILES_STORE);
    }
    if (!db.objectStoreNames.contains(CHUNKS_STORE)) {
      db.createObjectStore(CHUNKS_STORE);
    }
  };
  return requestToPromise(openRequest, "open", { db: ZIP_EXPORT_DB });
}

export function idbPut(
  db: IDBDatabase,
  storeName: string,
  key: number,
  value: unknown
): Promise<void> {
  // resolves on transaction commit rather than request success — request success
  // fires before the data is durable, and a worker.terminate() at that point
  // aborts the transaction and loses the write
  return new Promise((resolve, reject) => {
    const context = { store: storeName, key, value: describeStoredValue(value) };
    const transaction = db.transaction(storeName, "readwrite");
    const request = transaction.objectStore(storeName).put(value, key);
    transaction.oncomplete = () => resolve();
    // the failing request carries the specific reason (quota, unsupported value);
    // the transaction only reports that it did not commit
    request.onerror = () => reject(idbFailure("put", request.error, context));
    transaction.onerror = () =>
      reject(idbFailure("put transaction", transaction.error, context));
    transaction.onabort = () =>
      reject(idbFailure("put transaction abort", transaction.error, context));
  });
}

export function idbGet<T>(
  db: IDBDatabase,
  storeName: string,
  key: number
): Promise<T | undefined> {
  const store = db.transaction(storeName, "readonly").objectStore(storeName);
  return requestToPromise(store.get(key), "get", { store: storeName, key }) as Promise<
    T | undefined
  >;
}

export function idbGetAll<T>(db: IDBDatabase, storeName: string): Promise<T[]> {
  const store = db.transaction(storeName, "readonly").objectStore(storeName);
  // IDBObjectStore.getAll returns records in ascending key order, so
  // sequentially-keyed zip chunks come back in the order they were written
  return requestToPromise(store.getAll(), "getAll", { store: storeName }) as Promise<T[]>;
}

export function idbDelete(
  db: IDBDatabase,
  storeName: string,
  key: number
): Promise<void> {
  return new Promise((resolve, reject) => {
    const context = { store: storeName, key };
    const transaction = db.transaction(storeName, "readwrite");
    transaction.objectStore(storeName).delete(key);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(idbFailure("delete", transaction.error, context));
    transaction.onabort = () =>
      reject(idbFailure("delete abort", transaction.error, context));
  });
}

/** Reserved key for the probe below; real entries are keyed from 0 upwards */
const BLOB_PROBE_KEY = -1;

/**
 * WebKit cannot persist Blob/File values to IndexedDB when private browsing backs the
 * database in memory: every put fails with "UnknownError: Error preparing Blob/File
 * data to be stored in object store", whatever the size. Nothing advertises this, so
 * the only reliable check is to attempt one tiny write before committing to a format.
 */
export async function supportsBlobStorage(db: IDBDatabase): Promise<boolean> {
  try {
    await idbPut(db, FILES_STORE, BLOB_PROBE_KEY, new Blob(["probe"]));
  } catch (error) {
    console.warn("[zip-export] this browser cannot store Blobs in IndexedDB", error);
    return false;
  }
  try {
    await idbDelete(db, FILES_STORE, BLOB_PROBE_KEY);
  } catch (error) {
    // harmless: the probe record is keyed outside the entry range and every
    // export clears both stores before it starts writing
    console.warn("[zip-export] could not remove the Blob storage probe", error);
  }
  return true;
}

export function idbClearStores(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([FILES_STORE, CHUNKS_STORE], "readwrite");
    transaction.objectStore(FILES_STORE).clear();
    transaction.objectStore(CHUNKS_STORE).clear();
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(idbFailure("clear", transaction.error));
    transaction.onabort = () => reject(idbFailure("clear abort", transaction.error));
  });
}

// deliberately avoids indexedDB.deleteDatabase: WebKit/Safari can stall a
// deleteDatabase request indefinitely and then queues every later operation
// on the same database name behind it, deadlocking the export flow
export async function clearZipExportData(): Promise<void> {
  const db = await openZipExportDb();
  try {
    await idbClearStores(db);
  } finally {
    db.close();
  }
}
